import type { PayloadAction } from '@reduxjs/toolkit';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { nvFetch } from '../../shared/helpers/nvFetch';
import { isElectron } from '../../shared/helpers/platform';
import * as platform from '../../shared/helpers/platformApi';
import { ApiVcrmLicence } from '../../shared/types/VCRM';
import type { ThunkAPI } from '../store/types';
import { switchExtension } from './extensionThunks';

const getPartnerCrm = () => import('../helpers/partnerCrm');

interface VcrmState {
  isFetching: boolean;
  isRegistered: boolean;
  isRegistering: boolean;
  licenceId?: string;
  launchToken?: string;
  launchTokenExpires?: number;
}

const initialState: VcrmState = {
  isFetching: false,
  isRegistering: false,
  isRegistered: false,
  licenceId: undefined,
  launchToken: undefined,
  launchTokenExpires: undefined,
};

export const fetchLicences = createAsyncThunk<
  ApiVcrmLicence[] | undefined,
  { extensionId: string },
  ThunkAPI
>('vcrm/fetchLicences', async ({ extensionId }, thunkAPI) => {
  const customerId = thunkAPI.getState().client.activeCustomer?.id;

  const fetchResult = await nvFetch(
    `/customers/${customerId}/addons/vcrm/licences?extensionId=${extensionId}`,
  );
  const licences = (await fetchResult.json()).items as ApiVcrmLicence[];

  if (!licences?.length) {
    return;
  }

  return licences;
});

export const fetchLaunchToken = createAsyncThunk<
  { accessToken: string; expires: string } | undefined,
  void,
  ThunkAPI
>('vcrm/fetchLaunchToken', async (_, thunkAPI) => {
  const {
    vcrm: { licenceId },
    client: { activeCustomer },
  } = thunkAPI.getState();

  if (!licenceId) {
    return;
  }

  const customerId = activeCustomer?.id;

  const fetchResult = await nvFetch(
    `/customers/${customerId}/addons/vcrm/licences/${licenceId}/authenticate`,
    {
      method: 'POST',
    },
  );
  const tokenInfo = (await fetchResult.json()) as {
    accessToken: string;
    expires: string;
  };

  platform.vcrmAvailability(true);

  if (isElectron()) {
    const partnerCrm = await getPartnerCrm();
    partnerCrm.launch(tokenInfo.accessToken, activeCustomer?.country);
  }

  return tokenInfo;
});

const vcrmSlice = createSlice({
  name: 'vcrm',
  initialState,
  reducers: {
    registeredStateChanged: (
      state,
      action: PayloadAction<{
        isRegistered?: boolean;
        isRegistering?: boolean;
      }>,
    ) => {
      if (action.payload.isRegistered !== undefined) {
        state.isRegistered = action.payload.isRegistered;
      }
      if (action.payload.isRegistering !== undefined) {
        state.isRegistering = action.payload.isRegistering;
      }
      platform.vcrmConnection(action.payload);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchLicences.pending, (state) => {
        state.isFetching = true;
      })
      .addCase(
        fetchLicences.fulfilled,
        (state, action: PayloadAction<ApiVcrmLicence[] | undefined>) => {
          state.isFetching = false;
          const licence = action.payload;
          if (!licence) {
            return;
          }
          state.licenceId = licence[0]?.id;
        },
      )
      .addCase(fetchLicences.rejected, (state) => {
        state.isFetching = false;
      })
      .addCase(fetchLaunchToken.pending, (state) => {
        state.isFetching = true;
      })
      .addCase(
        fetchLaunchToken.fulfilled,
        (
          state,
          action: PayloadAction<
            { accessToken: string; expires: string } | undefined
          >,
        ) => {
          state.isFetching = false;
          const tokenInfo = action.payload;
          if (!tokenInfo) {
            return;
          }
          state.launchToken = tokenInfo.accessToken;
          state.launchTokenExpires = new Date(tokenInfo.expires).getTime();
        },
      )
      .addCase(fetchLaunchToken.rejected, (state) => {
        state.isFetching = false;
      })
      .addCase(switchExtension.pending, (state) => {
        state.licenceId = undefined;
        state.launchToken = undefined;
        state.launchTokenExpires = undefined;

        platform.vcrmAvailability(false);
      });
  },
});

export const { registeredStateChanged } = vcrmSlice.actions;

export default vcrmSlice.reducer;
